import { useRef } from 'react';
import { motion, useScroll, useTransform, type MotionValue } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Button, cn } from '@ibas/ui';
import { Magnetic } from '@/components/motion';
import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';

export interface HeroGalleryProps {
  className?: string;
}

interface BentoImage {
  src: string;
  alt: string;
  /** Tailwind span classes: mobile (2-col/3-row grid), then md+ (4-col/2-row grid). */
  span: string;
  /** How far into the scroll (0–1) this cell's settle animation begins. */
  delay: number;
  /** CSS object-position keeping the subject in frame under object-cover. */
  position: string;
}

const IMAGES: BentoImage[] = [
  {
    src: '/media/images/campus/campus-building-exterior.webp',
    alt: 'Exterior view of the modern copper-clad IBAS campus building, Thalwil',
    span: 'col-span-2 row-span-1 md:col-span-2 md:row-span-2',
    delay: 0,
    // Portrait building in a near-square feature cell — hold the facade.
    position: '50% 35%',
  },
  {
    src: '/media/images/people/students-classroom-candid.webp',
    alt: 'Two IBAS students in a classroom, one following the lecture while the other works at a laptop',
    span: 'col-span-1 row-span-1',
    delay: 0.06,
    // Keep both students' faces in frame in the small square-ish cell.
    position: '50% 40%',
  },
  {
    src: '/media/images/graduation-events/student-group-photo-outdoor-gravel-path.webp',
    alt: 'Large group photo of IBAS students posing together on a gravel path beside a tree-lined lawn',
    span: 'col-span-1 row-span-1',
    delay: 0.12,
    // Tall group shot cropped to the people — bias down past the tree canopy.
    position: '50% 55%',
  },
  {
    src: '/media/images/campus/zurich-lake-mountain-view.webp',
    alt: 'View across Lake Zürich towards the Alps near the IBAS campus',
    span: 'col-span-1 row-span-1',
    delay: 0.18,
    position: '50% 50%',
  },
  {
    src: '/media/images/graduation-events/graduation-ceremony-cap-toss-group-photo.webp',
    alt: 'Large group of IBAS graduates in academic gowns throwing their caps in celebration',
    span: 'col-span-1 row-span-1',
    delay: 0.24,
    // Centre the crowd and tossed caps in the small square-ish cell.
    position: '50% 45%',
  },
];

/**
 * BentoCell — a single gallery tile. Under motion, its wrapper scale/translateY
 * are scrubbed by the section's shared scroll progress (via inline style passed
 * from the parent); under reduced motion no style is applied and the tile simply
 * sits in its settled, static position. Only transform is animated — no layout
 * properties — so this stays smooth even with five concurrent tweens.
 */
function BentoCell({
  image,
  style,
}: {
  image: BentoImage;
  style?: { scale: MotionValue<number>; y: MotionValue<number> };
}) {
  return (
    <motion.div
      style={style}
      className={cn(
        'relative overflow-hidden rounded-xl bg-muted shadow-xl shadow-primary/20',
        image.span,
      )}
    >
      <img
        src={image.src}
        alt={image.alt}
        loading={image.delay === 0 ? 'eager' : 'lazy'}
        decoding="async"
        style={{ objectPosition: image.position }}
        className="h-full w-full object-cover"
      />
    </motion.div>
  );
}

/**
 * HeroGallery — scroll-driven bento gallery hero (adapted from the
 * hero-gallery-scroll-animation pattern). A sticky bento grid of five IBAS
 * campus/student images settles into place (scale + rise) as the section is
 * scrolled through. The centred title block starts hidden and reveals in ONCE
 * (a one-shot `whileInView` fade + rise, `once: true`) when the section enters
 * view, then holds fully visible over the assembled grid — its opacity is not
 * tied to scroll position, so it can never fade back out or flicker in and out.
 * The bento cells' settle is scroll-value driven via Framer's useScroll/
 * useTransform, per the ibas-motion techniques catalog (§8) which permits
 * Framer here since nothing is React-state driven.
 *
 * Reduced motion: the tall scroll-runway collapses to a single viewport height,
 * no transforms are applied to the grid, and the title renders in its settled,
 * fully legible state permanently — nothing here depends on scroll to be seen.
 */
export function HeroGallery({ className }: HeroGalleryProps) {
  const reduced = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // Title reveal is deliberately NOT scroll-linked: a scroll-driven opacity
  // reads as "appears then disappears" as you keep scrolling. Instead the
  // bulletproof rect-based `useInView` flips true ONCE when the block enters
  // view, and a plain CSS transition fades + rises it in and then HOLDS it
  // visible for good (the class never reverts). Reveals immediately under
  // reduced motion (useInView returns true when disabled).
  const titleInView = useInView(titleRef, { threshold: 0.85, once: true, disabled: reduced });

  // Fixed set of five cells: one useTransform pair per cell (rules-of-hooks
  // friendly since the count never changes), each starting its settle a touch
  // later than the last for a gentle cascading assembly.
  const cell0Scale = useTransform(
    scrollYProgress,
    [IMAGES[0].delay, IMAGES[0].delay + 0.5],
    [1.12, 1],
  );
  const cell0Y = useTransform(scrollYProgress, [IMAGES[0].delay, IMAGES[0].delay + 0.5], [28, 0]);
  const cell1Scale = useTransform(
    scrollYProgress,
    [IMAGES[1].delay, IMAGES[1].delay + 0.5],
    [1.12, 1],
  );
  const cell1Y = useTransform(scrollYProgress, [IMAGES[1].delay, IMAGES[1].delay + 0.5], [28, 0]);
  const cell2Scale = useTransform(
    scrollYProgress,
    [IMAGES[2].delay, IMAGES[2].delay + 0.5],
    [1.12, 1],
  );
  const cell2Y = useTransform(scrollYProgress, [IMAGES[2].delay, IMAGES[2].delay + 0.5], [28, 0]);
  const cell3Scale = useTransform(
    scrollYProgress,
    [IMAGES[3].delay, IMAGES[3].delay + 0.5],
    [1.12, 1],
  );
  const cell3Y = useTransform(scrollYProgress, [IMAGES[3].delay, IMAGES[3].delay + 0.5], [28, 0]);
  const cell4Scale = useTransform(
    scrollYProgress,
    [IMAGES[4].delay, IMAGES[4].delay + 0.5],
    [1.12, 1],
  );
  const cell4Y = useTransform(scrollYProgress, [IMAGES[4].delay, IMAGES[4].delay + 0.5], [28, 0]);

  const cellStyles = [
    { scale: cell0Scale, y: cell0Y },
    { scale: cell1Scale, y: cell1Y },
    { scale: cell2Scale, y: cell2Y },
    { scale: cell3Scale, y: cell3Y },
    { scale: cell4Scale, y: cell4Y },
  ];

  return (
    <div
      ref={containerRef}
      className={cn('relative', reduced ? 'min-h-screen' : 'h-[200vh]', className)}
    >
      <div
        className={cn(
          'flex w-full items-center justify-center overflow-hidden bg-brand-navy',
          reduced ? 'relative min-h-screen' : 'sticky top-0 h-screen',
        )}
      >
        {/* Bento image grid */}
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-3 gap-3 p-4 sm:gap-4 sm:p-6 md:grid-cols-4 md:grid-rows-2 md:gap-5 md:p-10">
          {IMAGES.map((image, i) => (
            <BentoCell key={image.src} image={image} style={reduced ? undefined : cellStyles[i]} />
          ))}
        </div>

        {/* Scrim for title legibility over imagery */}
        <div className="absolute inset-0 bg-primary/55" aria-hidden="true" />

        {/* Centred title block — reveals once (via useInView) then holds. */}
        <div
          ref={titleRef}
          className={cn(
            'relative z-10 mx-auto max-w-xl px-6 text-center transition-all duration-700 ease-out motion-reduce:transition-none',
            titleInView ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0',
          )}
        >
          <h1 className="text-balance font-display text-4xl font-medium leading-[1.05] text-primary-foreground sm:text-5xl md:text-6xl">
            Graduate with Swiss quality
          </h1>
          <p className="mt-4 font-sans text-base text-primary-foreground/80 sm:text-lg">
            One programme. Two qualifications. Broader leverage.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Magnetic>
              {/* Gold CTA: a navy (primary) button would blend into the navy
                  hero — the gold/secondary variant (gold bg + navy text) reads
                  as the clear primary action here. */}
              <Button variant="secondary" size="lg" className="gap-2">
                Explore our programmes
                <ArrowRight className="size-4" aria-hidden="true" />
              </Button>
            </Magnetic>
            <Button
              variant="link"
              size="lg"
              className="text-primary-foreground hover:text-brand-gold"
            >
              Take a quick tour
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
